import React, { useState, useRef } from "react";
import Dialog from "@mui/material/Dialog";
import { notification, Typography } from "antd";
import * as XLSX from "xlsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimesCircle, faCheckCircle, faExclamationCircle, faCloudDownload } from "@fortawesome/free-solid-svg-icons";
import { Link } from "react-router-dom";
import moment from "moment";
import { updateDueAmount } from "../../../controllers/V1/users/usersController";

const mapApiMessage = (message) => {
  if (!message) return "System error";
  if (message.toLowerCase().includes("no data found")) return "User not found";
  return message;
};

const validateRow = (row) => {
  const posNumber = row.pos_number !== undefined && row.pos_number !== null && String(row.pos_number).trim() !== "";
  const mobileNumber = row.mobile_number !== undefined && row.mobile_number !== null && String(row.mobile_number).trim() !== "";
  const dueAmount = row.due_amount;

  if (!posNumber && !mobileNumber) {
    return { valid: false, message: "POS number or mobile number is required." };
  }
  if (posNumber && isNaN(parseInt(row.pos_number))) {
    return { valid: false, message: "Invalid POS number" };
  }
  if (mobileNumber && isNaN(parseInt(row.mobile_number))) {
    return { valid: false, message: "Invalid mobile number" };
  }
  if (dueAmount === undefined || dueAmount === null || String(dueAmount).trim() === "") {
    return { valid: false, message: "Due amount is required." };
  }
  if (isNaN(parseFloat(dueAmount))) {
    return { valid: false, message: "Invalid due amount" };
  }
  return { valid: true, message: "" };
};

const DueAmountUpdatePopup = ({ popUpOpen, togglePopUp, getList }) => {
  const fileRef = useRef(null);
  const [updateType, setUpdateType] = useState("single");
  const [errors, setErrors] = useState({});
  const [previewRows, setPreviewRows] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [summary, setSummary] = useState(null);
  const [isBulkComplete, setIsBulkComplete] = useState(false);

  const resetState = () => {
    setUpdateType("single");
    setErrors({});
    setPreviewRows([]);
    setIsProcessing(false);
    setSummary(null);
    setIsBulkComplete(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const handleCancel = () => {
    resetState();
    togglePopUp();
  };

  const downloadTemplate = () => {
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet([
      { pos_number: "", mobile_number: "", due_amount: "" },
    ]);
    XLSX.utils.book_append_sheet(workbook, worksheet, "Due Amount");
    XLSX.writeFile(workbook, `due_amount_template_${moment().format("YYYY-MM-DD")}.xlsx`);
  };

  const handleChangeFile = (e) => {
    try {
      const file = e.target.files[0];
      if (!file) return;

      const isExcel =
        file.type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
        file.name.endsWith(".xlsx");

      if (!isExcel) {
        notification.open({
          message: "Oops!",
          description: "Please upload a valid Excel file (.xlsx)",
          placement: "topRight",
          icon: <FontAwesomeIcon icon={faExclamationCircle} style={{ color: "red" }} />,
          duration: 2,
        });
        e.target.value = null;
        return;
      }

      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          const data = new Uint8Array(event.target.result);
          const workbook = XLSX.read(data, { type: "array" });
          const sheetName = workbook.SheetNames[0];
          const sheet = workbook.Sheets[sheetName];
          const json = XLSX.utils.sheet_to_json(sheet);

          if (json.length === 0) {
            notification.open({
              message: "Oops!",
              description: "The Excel file is empty or has no valid data.",
              placement: "topRight",
              icon: <FontAwesomeIcon icon={faExclamationCircle} style={{ color: "red" }} />,
              duration: 2,
            });
            e.target.value = null;
            return;
          }

          const rows = json.map((item, index) => {
            const row = {
              rowNumber: index + 1,
              pos_number: item.pos_number ?? "",
              mobile_number: item.mobile_number ?? "",
              due_amount: item.due_amount ?? "",
              status: "Pending",
              message: "",
            };
            const validation = validateRow(row);
            if (!validation.valid) {
              row.status = "Failed";
              row.message = validation.message;
            }
            return row;
          });

          setPreviewRows(rows);
          setSummary(null);
          setErrors({});
        } catch (parseError) {
          notification.open({
            message: "Oops!",
            description: "Failed to parse Excel file. Please check the file format.",
            placement: "topRight",
            icon: <FontAwesomeIcon icon={faExclamationCircle} style={{ color: "red" }} />,
            duration: 2,
          });
          e.target.value = null;
        }
      };
      reader.readAsArrayBuffer(file);
    } catch (error) {
      notification.open({
        message: "Oops!",
        description: "Something went wrong. Please try again.",
        placement: "topRight",
        icon: <FontAwesomeIcon icon={faExclamationCircle} style={{ color: "red" }} />,
        duration: 2,
      });
    }
  };

  const handleSingleSubmit = async (e) => {
    e.preventDefault();
    try {
      const form = new FormData(e.target);
      const identifier = (form.get("identifier") || "").trim();
      const dueAmount = form.get("due_amount");

      const fieldErrors = {};
      if (!identifier) {
        fieldErrors.identifier = "POS number or mobile number is required.";
      }
      if (dueAmount === null || dueAmount === "") {
        fieldErrors.due_amount = "Due amount is required.";
      } else if (isNaN(parseFloat(dueAmount))) {
        fieldErrors.due_amount = "Due amount must be numeric.";
      } else if (identifier && isNaN(parseInt(identifier))) {
        fieldErrors.identifier = "Invalid POS number or mobile number.";
      }

      if (Object.keys(fieldErrors).length > 0) {
        setErrors(fieldErrors);
        return;
      }

      setErrors({});
      setIsProcessing(true);

      let res = await updateDueAmount({
        pos_number: identifier,
        mobile_number: "",
        due_amount: dueAmount,
      });

      if (res.status !== true) {
        res = await updateDueAmount({
          pos_number: "",
          mobile_number: identifier,
          due_amount: dueAmount,
        });
      }

      if (res.status === true) {
        notification.open({
          message: "Success",
          description: "Due amount updated successfully.",
          placement: "topRight",
          icon: <FontAwesomeIcon icon={faCheckCircle} style={{ color: "green" }} />,
          duration: 2,
        });
        if (getList) getList();
        handleCancel();
      } else {
        notification.open({
          message: "Oops!",
          description: mapApiMessage(res.message),
          placement: "topRight",
          icon: <FontAwesomeIcon icon={faExclamationCircle} style={{ color: "red" }} />,
          duration: 2,
        });
      }
      setIsProcessing(false);
    } catch (error) {
      setIsProcessing(false);
      notification.open({
        message: "Oops!",
        description: "System error",
        placement: "topRight",
        icon: <FontAwesomeIcon icon={faExclamationCircle} style={{ color: "red" }} />,
        duration: 2,
      });
    }
  };

  const handleBulkSubmit = async () => {
    if (previewRows.length === 0) {
      notification.open({
        message: "Oops!",
        description: "Please upload an Excel file with records.",
        placement: "topRight",
        icon: <FontAwesomeIcon icon={faExclamationCircle} style={{ color: "red" }} />,
        duration: 2,
      });
      return;
    }

    setIsProcessing(true);
    setSummary(null);
    let successCount = 0;
    let failedCount = 0;
    const rows = [...previewRows];

    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];

      if (row.status === "Failed" && row.message) {
        failedCount += 1;
        continue;
      }

      rows[i] = { ...row, status: "Processing", message: "" };
      setPreviewRows([...rows]);

      const hasPos = row.pos_number !== undefined && row.pos_number !== null && String(row.pos_number).trim() !== "";
      const option = {
        pos_number: hasPos ? row.pos_number : "",
        mobile_number: hasPos ? "" : row.mobile_number,
        due_amount: row.due_amount,
      };

      try {
        const res = await updateDueAmount(option);
        if (res.status === true) {
          successCount += 1;
          rows[i] = { ...rows[i], status: "Completed", message: "" };
        } else {
          failedCount += 1;
          rows[i] = { ...rows[i], status: "Failed", message: mapApiMessage(res.message) };
        }
      } catch (error) {
        failedCount += 1;
        rows[i] = { ...rows[i], status: "Failed", message: "System error" };
      }
      setPreviewRows([...rows]);
    }

    setSummary({
      total: previewRows.length,
      success: successCount,
      failed: failedCount,
    });
    setIsBulkComplete(true);
    setIsProcessing(false);
    if (getList) getList();
  };

  return (
    <Dialog
      open={popUpOpen}
      onClose={handleCancel}
      fullWidth={true}
      maxWidth={"md"}
      PaperProps={{
        className: "myDialogPopUp",
      }}
    >
      <div className="myDrawer">
        <div className="inventory_popup_title">
          <Typography
            variant="h5"
            component="div"
            gutterBottom
            className="inventory_popup_title_heading"
          >
            Update Due Amount
          </Typography>
          <Link
            to={false}
            style={{ position: "absolute", right: 8, top: 8, color: "#dd5151" }}
            onClick={handleCancel}
          >
            <FontAwesomeIcon icon={faTimesCircle} />
          </Link>
        </div>

        <Typography variant="body1" component="div" className="my_delete_popup_paragraph">
          <div className="container-fluid">
            <div className="categories_open_popup_main_contnet">
              {!isBulkComplete && (
                <div className="row">
                  <div className="col-lg-12 col-md-12 col-sm-12 col-xs-12">
                    <div className="inventory_open_popup_action_btn">
                      <button
                        style={{ width: "150px" }}
                        type="button"
                        className={updateType === "single" ? "btn btn-dark" : "btn btn-light categories_cancel_button"}
                        onClick={() => {
                          setUpdateType("single");
                          setErrors({});
                          setSummary(null);
                        }}
                        disabled={isProcessing}
                      >
                        Single Update
                      </button>
                      <button
                        style={{ width: "150px" }}
                        type="button"
                        className={updateType === "bulk" ? "btn btn-dark" : "btn btn-light categories_cancel_button"}
                        onClick={() => {
                          setUpdateType("bulk");
                          setErrors({});
                        }}
                        disabled={isProcessing}
                      >
                        Bulk Update
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {updateType === "single" && (
                <form onSubmit={handleSingleSubmit}>
                  <div className="form-group row">
                    <div className="col-lg-6 col-md-6 col-sm-12 col-xs-12">
                      <label htmlFor="identifier" className="all_lable_for_vendor_dashbaord">
                        <span style={{ color: "red" }}>*</span> POS Number OR Mobile Number :
                      </label>
                      <input
                        type="text"
                        name="identifier"
                        id="identifier"
                        placeholder="Enter POS number or mobile number"
                        className="form-control categories_select_tg_enter_product"
                        disabled={isProcessing}
                      />
                      {errors?.identifier ? <span className="text-danger">{errors.identifier}</span> : ""}
                    </div>
                    <div className="col-lg-6 col-md-6 col-sm-12 col-xs-12">
                      <label htmlFor="due_amount" className="all_lable_for_vendor_dashbaord">
                        <span style={{ color: "red" }}>*</span> Due Amount :
                      </label>
                      <input
                        type="number"
                        name="due_amount"
                        id="due_amount"
                        placeholder="Enter due amount"
                        className="form-control categories_select_tg_enter_product"
                        step="any"
                        disabled={isProcessing}
                      />
                      {errors?.due_amount ? <span className="text-danger">{errors.due_amount}</span> : ""}
                    </div>
                    <div className="col-lg-12 col-md-12 col-sm-12 col-xs-12">
                      <div className="inventory_open_popup_action_btn">
                        <button
                          type="button"
                          className="btn btn-light categories_cancel_button"
                          onClick={handleCancel}
                          disabled={isProcessing}
                        >
                          Cancel
                        </button>
                        <button type="submit" className="btn btn-danger" disabled={isProcessing}>
                          Submit
                        </button>
                      </div>
                    </div>
                  </div>
                </form>
              )}

              {updateType === "bulk" && (
                <div className="form-group row">
                  {!isBulkComplete && (
                    <>
                      <div className="col-lg-6 col-md-6 col-sm-12 col-xs-12">
                        <label className="all_lable_for_vendor_dashbaord">Sample Template :</label>
                        <button
                          type="button"
                          className="form-control categories_select_tg_enter_product"
                          style={{ textAlign: "left", cursor: "pointer" }}
                          onClick={downloadTemplate}
                          disabled={isProcessing}
                        >
                          <FontAwesomeIcon icon={faCloudDownload} /> Download Sample Template
                        </button>
                      </div>
                      <div className="col-lg-6 col-md-6 col-sm-12 col-xs-12">
                        <label htmlFor="due_amount_file" className="all_lable_for_vendor_dashbaord">
                          <span style={{ color: "red" }}>*</span> Upload Excel (.xlsx) :
                        </label>
                        <input
                          type="file"
                          name="due_amount_file"
                          id="due_amount_file"
                          ref={fileRef}
                          accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                          onChange={handleChangeFile}
                          className="form-control categories_select_tg_enter_product"
                          disabled={isProcessing}
                        />
                      </div>
                    </>
                  )}

                  <div
                    className="col-lg-12 col-md-12 col-sm-12 col-xs-12"
                    style={{
                      maxHeight: "280px",
                      overflowY: "auto",
                      border: "1px solid #ddd",
                      marginTop: "10px",
                    }}
                  >
                    <table className="table table-bordered" id="dueAmountPreviewTable" width="100%" cellSpacing="0">
                      <thead>
                        <tr>
                          <th>Row Number</th>
                          <th>POS Number</th>
                          <th>Mobile Number</th>
                          <th>Due Amount</th>
                          <th>Status</th>
                          <th>Message</th>
                        </tr>
                      </thead>
                      <tbody>
                        {previewRows?.length > 0 ? (
                          previewRows.map((row, index) => (
                            <tr key={index}>
                              <td>{row.rowNumber}</td>
                              <td>{row.pos_number}</td>
                              <td>{row.mobile_number}</td>
                              <td>{row.due_amount}</td>
                              <td>{row.status || "Pending"}</td>
                              <td>{row.message}</td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="6" className="img-center">
                              No data found !
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {summary && (
                    <div className="col-lg-12 col-md-12 col-sm-12 col-xs-12" style={{ marginTop: "10px" }}>
                      <h5>Summary</h5>
                      <p>Total Records: {summary.total}</p>
                      <p style={{ color: "green" }}>Success Count: {summary.success}</p>
                      <p style={{ color: "red" }}>Failed Count: {summary.failed}</p>
                    </div>
                  )}

                  <div className="col-lg-12 col-md-12 col-sm-12 col-xs-12">
                    <div className="inventory_open_popup_action_btn">
                      {isBulkComplete ? (
                        <button
                          type="button"
                          className="btn btn-dark"
                          onClick={handleCancel}
                        >
                          Close
                        </button>
                      ) : (
                        <>
                          <button
                            type="button"
                            className="btn btn-light categories_cancel_button"
                            onClick={handleCancel}
                            disabled={isProcessing}
                          >
                            Cancel
                          </button>
                          <button
                            type="button"
                            className="btn btn-danger"
                            onClick={handleBulkSubmit}
                            disabled={isProcessing || previewRows.length === 0}
                          >
                            {isProcessing ? "Processing..." : "Submit"}
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </Typography>
      </div>
    </Dialog>
  );
};

export default DueAmountUpdatePopup;
